import { motion } from 'framer-motion';
import Link from 'next/link';

/**
 * SolutionOverviewSection — three product tiles, billboard pattern.
 *
 * Each tile shows a real product capture in a 16:10 browser frame, marked with
 * data-screenshot-slot. On mobile the tiles collapse into a scroll-snap row.
 *
 * Captures are picked to be legible at tile size on a dark page, which rules
 * out the older light-mode screenshots (embed/pricing/production.png) — they
 * punched a white hole in the card and their densest content sat off-frame.
 * `focal` sets object-position per tile because the three captures have very
 * different shapes: the two tall ones read best from the top (app header +
 * first rows), while the wide formula modal has to anchor left or the crop eats
 * the prompt text that makes the tile make sense.
 */
export function SolutionOverviewSection({ isDarkMode }) {
  const d = isDarkMode;

  const tiles = [
    {
      label: 'Storefront',
      title: 'Sell custom without a spreadsheet.',
      blurb: 'Live pricing, real stock, your domain.',
      href: '/product/commerce',
      slotId: 'storefront-tile',
      screenshot: '/screenshots/storefront-catalog.png',
      focal: 'object-top',
    },
    {
      label: 'Pricing',
      title: 'Price and configure, without a dev.',
      blurb: 'Plain-English rules become live formulas.',
      // NOTE: /product/pricing-engine 404s — that Strapi slug was never
      // published, so the middleware rewrite has nothing to point at. Sending
      // this tile to the product hub until the page ships. The same dead link
      // still exists in OneSystemSection and DifferentiatorSection (homepage).
      href: '/product',
      slotId: 'pricing-tile',
      screenshot: '/screenshots/pricing-formula.png',
      focal: 'object-left-top',
    },
    {
      label: 'Production',
      title: 'Orders land on the floor ready to build.',
      blurb: 'Build sheets and shipping from one record.',
      href: '/product/production',
      slotId: 'production-tile',
      screenshot: '/screenshots/production-tracking.png',
      focal: 'object-top',
    },
  ];

  return (
    <section
      id='solution'
      className={`py-20 md:py-28 relative overflow-hidden ${
        d
          ? 'bg-gradient-to-b from-zinc-950 via-zinc-900/80 to-zinc-950'
          : 'bg-gradient-to-b from-white via-slate-50/80 to-white'
      }`}
    >
      <div className='tc-container max-w-6xl relative z-10'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className='text-center mb-14 md:mb-16'
        >
          <span
            className={`inline-block px-3 py-1 rounded-full text-[11px] font-sans tracking-[0.25em] uppercase border mb-5 ${
              d
                ? 'bg-jade-deep/15 text-jade-soft border-jade-deep/30'
                : 'bg-jade-deep/10 text-jade-deep border-jade-deep/30'
            }`}
          >
            How we solve it
          </span>
          <h2
            className={`text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight font-display [text-wrap:balance] ${
              d ? 'text-white' : 'text-gray-900'
            }`}
          >
            One platform.{' '}
            <span
              className={`bg-gradient-to-r ${
                d
                  ? 'from-jade-soft via-jade to-jade-soft'
                  : 'from-jade-deep via-jade-deep to-jade-deep'
              } bg-clip-text text-transparent`}
            >
              Three jobs done right.
            </span>
          </h2>
        </motion.div>

        {/* Tiles — desktop grid, mobile snap row */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 max-md:flex max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:-mx-6 max-md:px-6 max-md:pb-2'>
          {tiles.map((t, i) => (
            <motion.div
              key={t.href}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className='max-md:snap-start max-md:shrink-0 max-md:w-[85vw]'
            >
              <Link
                href={t.href}
                className={`group block h-full rounded-2xl border overflow-hidden transition-all ${
                  d
                    ? 'bg-zinc-900/60 border-zinc-800 hover:border-jade-deep/50 hover:bg-zinc-900/80'
                    : 'bg-white border-gray-200 hover:border-jade-deep/50 hover:shadow-lg'
                }`}
              >
                {/* Browser-frame mock for the screenshot slot */}
                <div
                  data-screenshot-slot={t.slotId}
                  className={`relative aspect-[16/10] overflow-hidden border-b ${
                    d ? 'bg-zinc-950 border-zinc-800' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {/* Faux browser chrome */}
                  <div
                    className={`absolute top-0 inset-x-0 h-6 flex items-center gap-1.5 px-3 z-10 ${
                      d ? 'bg-zinc-900/90' : 'bg-gray-100'
                    }`}
                  >
                    <span className='w-2 h-2 rounded-full bg-red-400/60' />
                    <span className='w-2 h-2 rounded-full bg-yellow-400/60' />
                    <span className='w-2 h-2 rounded-full bg-green-400/60' />
                  </div>
                  {/* Sits below the faux chrome (h-6), not behind it. The
                      previous `inset-0 … mt-6` kept a full-height box and shoved
                      it down 24px, clipping the bottom of every capture.
                      Height is explicit rather than `top-6 bottom-0`: an
                      absolutely-positioned <img> is a replaced element, so
                      height:auto resolves to its intrinsic height and `bottom`
                      is ignored — each tile would size to its own screenshot. */}
                  <img
                    src={t.screenshot}
                    alt=''
                    aria-hidden='true'
                    className={`absolute top-6 inset-x-0 w-full h-[calc(100%-1.5rem)] object-cover ${t.focal}`}
                    loading='lazy'
                    decoding='async'
                  />
                </div>

                {/* Body */}
                <div className='p-6 md:p-7'>
                  <div
                    className={`text-[10px] font-sans tracking-[0.25em] uppercase mb-2 ${
                      d ? 'text-zinc-500' : 'text-gray-400'
                    }`}
                  >
                    {t.label}
                  </div>
                  <h3
                    className={`text-lg md:text-xl font-bold tracking-tight mb-3 leading-snug ${
                      d ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {t.title}
                  </h3>
                  <p
                    className={`text-sm md:text-[15px] leading-relaxed mb-5 ${
                      d ? 'text-zinc-400' : 'text-gray-600'
                    }`}
                  >
                    {t.blurb}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1.5 text-sm font-medium transition-transform group-hover:translate-x-1 ${
                      d ? 'text-jade-soft' : 'text-jade-deep'
                    }`}
                  >
                    See how it works
                    <svg
                      className='h-4 w-4'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M14 5l7 7m0 0l-7 7m7-7H3'
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
